import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../contexts/AuthContext';
import { shJson } from './api';
import ContratoMultiSelect, {
  parseContratosSelection,
  serializeContratosSelection,
  shouldUseContratosMultiSelect,
} from './ContratoMultiSelect';
import SelecaoMultiplaCheckbox, {
  getManualSelecaoItems,
  getQuestionSelectionMode,
  parseMultiSelection,
  serializeMultiSelection,
} from './SelecaoMultiplaCheckbox';

const STATUS_OPTS = ['OK', 'Atenção', 'Crítico', 'N/A'];

export default function SafetyHuddleFormPage() {
  const { sectorId, templateId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [referenceDate, setReferenceDate] = useState(() => new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await shJson(`/api/safety-huddle/templates/${templateId}`, unidadeSelecionada);
      setTemplate(data);
      const init = {};
      (data.questions || []).forEach((q) => {
        const contratos = shouldUseContratosMultiSelect(q, data.sector);
        const multi = getQuestionSelectionMode(q) === 'multiple';
        init[q.id] = {
          questionId: q.id,
          statusValue: '',
          checkboxValue: false,
          textValue: '',
          numberValue: '',
          dateValue: '',
          notes: '',
          contratosSelected: contratos ? [] : undefined,
          multiSelected: !contratos && multi ? [] : undefined,
        };
      });
      setAnswers(init);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [templateId, unidadeSelecionada]);

  useEffect(() => {
    load();
  }, [load]);

  const setAnswer = (qid, patch) => {
    setAnswers((prev) => ({ ...prev, [qid]: { ...prev[qid], ...patch } }));
  };

  const isAnswerFilled = (q, a) => {
    if (!a) return false;
    if (q.responseType === 'status') return !!(a.statusValue && String(a.statusValue).trim());
    if (q.responseType === 'checkbox') return !!a.checkboxValue;
    if (q.responseType === 'numero') return a.numberValue !== '' && a.numberValue != null;
    if (q.responseType === 'selecao') {
      if (a.contratosSelected) return a.contratosSelected.length > 0;
      if (a.multiSelected) return a.multiSelected.length > 0;
      return !!(a.textValue && String(a.textValue).trim());
    }
    if (q.responseType === 'data') return !!(a.dateValue && String(a.dateValue).trim());
    return !!(a.textValue && String(a.textValue).trim());
  };

  const submit = async () => {
    setSaving(true);
    setError('');
    setOk('');
    try {
      const missing = (template?.questions || [])
        .filter((q) => q.required && !isAnswerFilled(q, answers[q.id]))
        .map((q) => q.label);
      if (missing.length) {
        setError(`Preencha as perguntas obrigatórias: ${missing.join('; ')}`);
        setSaving(false);
        return;
      }

      const payload = {
        referenceDate,
        answers: Object.values(answers).map((a) => {
          let textValue = a.textValue || null;
          if (a.contratosSelected) {
            textValue = serializeContratosSelection(a.contratosSelected) || null;
          } else if (a.multiSelected) {
            textValue = serializeMultiSelection(a.multiSelected) || null;
          }
          return {
            questionId: a.questionId,
            statusValue: a.statusValue || null,
            checkboxValue: a.checkboxValue,
            textValue,
            numberValue: a.numberValue === '' ? null : Number(a.numberValue),
            dateValue: a.dateValue || null,
            notes: a.notes || null,
          };
        }),
      };
      const res = await shJson(
        `/api/safety-huddle/templates/${templateId}/submissions`,
        unidadeSelecionada,
        { method: 'POST', body: JSON.stringify(payload) }
      );
      setOk(`Registro #${res.id} salvo com sucesso.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/safety-huddle/setores/${sectorId}`)}
        sx={{ mb: 2 }}
      >
        Voltar ao setor
      </Button>

      <Typography variant="h5" fontWeight={700} color="primary.main">
        {template?.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {template?.description}
        {template?.sector?.name ? ` · ${template.sector.name}` : ''}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {ok && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>
          {ok}
        </Alert>
      )}

      <TextField
        type="date"
        label="Data de referência"
        InputLabelProps={{ shrink: true }}
        value={referenceDate}
        onChange={(e) => setReferenceDate(e.target.value)}
        sx={{ mb: 3, maxWidth: 220 }}
      />

      {(template?.questions || []).map((q) => {
        const contratos = shouldUseContratosMultiSelect(q, template?.sector);
        const mode = getQuestionSelectionMode(q);
        const manualItems = getManualSelecaoItems(q);
        const multiManual =
          q.responseType === 'selecao' && !contratos && mode === 'multiple';
        const singleManual =
          q.responseType === 'selecao' && !contratos && mode !== 'multiple';

        return (
          <Box
            key={q.id}
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: 2,
              border: '1px solid rgba(25,118,210,0.12)',
              bgcolor: q.isSubjectIdentifier ? 'rgba(25,118,210,0.04)' : 'transparent',
            }}
          >
            <Typography fontWeight={600} sx={{ mb: 1 }}>
              {q.label}
              {q.required ? (
                <Typography component="span" color="error.main" fontWeight={700}>
                  {' '}
                  *
                </Typography>
              ) : null}
              {q.isSubjectIdentifier ? ' · identificador' : ''}
              {multiManual ? ' · múltipla' : ''}
            </Typography>

            {q.responseType === 'status' && (
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={answers[q.id]?.statusValue || ''}
                  onChange={(e) => setAnswer(q.id, { statusValue: e.target.value })}
                >
                  {STATUS_OPTS.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {q.responseType === 'checkbox' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!answers[q.id]?.checkboxValue}
                    onChange={(e) => setAnswer(q.id, { checkboxValue: e.target.checked })}
                  />
                }
                label="Sim / conferido"
              />
            )}

            {contratos && (
              <ContratoMultiSelect
                value={
                  answers[q.id]?.contratosSelected ||
                  parseContratosSelection(answers[q.id]?.textValue)
                }
                onChange={(next) => setAnswer(q.id, { contratosSelected: next })}
              />
            )}

            {multiManual && (
              <SelecaoMultiplaCheckbox
                options={manualItems}
                value={
                  answers[q.id]?.multiSelected ||
                  parseMultiSelection(answers[q.id]?.textValue)
                }
                onChange={(next) => setAnswer(q.id, { multiSelected: next })}
              />
            )}

            {singleManual && manualItems.length > 0 && (
              <TextField
                fullWidth
                size="small"
                select
                value={answers[q.id]?.textValue || ''}
                onChange={(e) => setAnswer(q.id, { textValue: e.target.value })}
              >
                {manualItems.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {singleManual && !manualItems.length && (
              <TextField
                fullWidth
                size="small"
                value={answers[q.id]?.textValue || ''}
                onChange={(e) => setAnswer(q.id, { textValue: e.target.value })}
                helperText="Nenhuma opção configurada"
              />
            )}

            {q.responseType === 'texto' && (
              <TextField
                fullWidth
                size="small"
                value={answers[q.id]?.textValue || ''}
                onChange={(e) => setAnswer(q.id, { textValue: e.target.value })}
              />
            )}

            {q.responseType === 'numero' && (
              <TextField
                fullWidth
                size="small"
                type="number"
                value={answers[q.id]?.numberValue ?? ''}
                onChange={(e) => setAnswer(q.id, { numberValue: e.target.value })}
              />
            )}

            {q.responseType === 'data' && (
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data"
                InputLabelProps={{ shrink: true }}
                value={answers[q.id]?.dateValue || ''}
                onChange={(e) => setAnswer(q.id, { dateValue: e.target.value })}
                sx={{ maxWidth: 280 }}
              />
            )}

            <TextField
              fullWidth
              size="small"
              label="Observação (opcional)"
              sx={{ mt: 1 }}
              value={answers[q.id]?.notes || ''}
              onChange={(e) => setAnswer(q.id, { notes: e.target.value })}
            />
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button variant="contained" disabled={saving} onClick={submit}>
          {saving ? 'Salvando…' : 'Enviar registro'}
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate(`/safety-huddle/setores/${sectorId}/dashboard/${templateId}`)}
        >
          Ver dashboard
        </Button>
      </Box>
    </Box>
  );
}
